import React, {FormEvent, useCallback, useEffect, useMemo, useState} from "react";
import {api} from "../api";
import type {AdminUser, Role} from "../types";
import {useTranslation} from "react-i18next";
import ActionMenu, {type IMenuAction} from "./ActionMenu";
import Pagination from "./Pagination";
import {readPaginationPreference, writePaginationPreference} from "../paginationPreferences";
import {clearFormDraft, readFormDraft, sameFormValue, writeFormDraft} from "../formDrafts";
import useDialogFocus from "./useDialogFocus";


type Props = {currentUser: AdminUser; permissions: string[]};
type FormState = {userId: string; email: string; displayName: string; roles: string[]; active: boolean; sendInvite: boolean};
const emptyForm: FormState = {userId: "", email: "", displayName: "", roles: [], active: true, sendInvite: false};
const paginationKey = "administrators";
const administratorDraftKey = (user?: AdminUser | null) => `admin-id-express-form-draft:administrator:${user?.id || "create"}`;

export default function AdministratorsPanel({currentUser, permissions}: Props) {
  const {t} = useTranslation();
  const initialPagination = useMemo(() => readPaginationPreference(paginationKey), []);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pageError, setPageError] = useState("");
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState<AdminUser | null | undefined>(undefined);
  const [removing, setRemoving] = useState<AdminUser | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formBaseline, setFormBaseline] = useState<FormState>(emptyForm);
  const [pagination, setPagination] = useState({currentPage: initialPagination.page, totalPages: 0, count: 0});
  const [itemsPerPage, setItemsPerPage] = useState(initialPagination.pageSize);
  const [identityLookup, setIdentityLookup] = useState<"idle" | "checking" | "found" | "missing" | "error">("idle");
  const can = (permission: string) => permissions.includes("*") || permissions.includes(permission);

  const load = useCallback(async (currentPage = 1, nextItemsPerPage = itemsPerPage) => {
    setItemsPerPage(nextItemsPerPage);
    writePaginationPreference(paginationKey, {page: currentPage, pageSize: nextItemsPerPage});
    setLoading(true); setPageError("");
    try {
      const response = await api.get("/administrators", {params: {current_page: currentPage, items_per_page: nextItemsPerPage}});
      setUsers(response.data.data || []); setRoles(response.data.roles || []);
      setPagination({
        currentPage: Number(response.data.current_page || currentPage),
        totalPages: Number(response.data.total_pages || 0),
        count: Number(response.data.count || 0),
      });
    } catch (requestError: any) {
      setPageError(requestError.response?.status === 403 ? t("administrators.denied") : t("administrators.genericError"));
    } finally { setLoading(false); }
  }, [itemsPerPage, t]);

  useEffect(() => { load(initialPagination.page, initialPagination.pageSize); }, []);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? users.filter(user => [user.displayName, user.email, user.userId, ...user.roles]
      .some(value => String(value || "").toLowerCase().includes(needle))) : users;
  }, [query, users]);

  const formDirty = useMemo(() => !sameFormValue(form, formBaseline), [form, formBaseline]);
  const currentDraftKey = editing !== undefined ? administratorDraftKey(editing) : "";

  useEffect(() => {
    if (editing === undefined || !currentDraftKey) return;
    if (formDirty) writeFormDraft(currentDraftKey, form);
    else clearFormDraft(currentDraftKey);
  }, [currentDraftKey, editing, form, formDirty]);

  const openCreate = () => {
    setFormBaseline(emptyForm);
    setForm(readFormDraft(administratorDraftKey(null), emptyForm));
    setIdentityLookup("idle"); setFormError(""); setPageError(""); setNotice(""); setEditing(null);
  };
  const openEdit = (user: AdminUser) => {
    const baseline = {userId: user.userId, email: user.email, displayName: user.displayName || "", roles: [...user.roles], active: user.active, sendInvite: false};
    setFormBaseline(baseline);
    setForm(readFormDraft(administratorDraftKey(user), baseline));
    setFormError(""); setPageError(""); setNotice(""); setEditing(user);
  };
  const closeEditor = useCallback(() => {
    if (busy) return;
    if (formDirty && !window.confirm(t("forms.closeDirtyConfirm"))) return;
    setEditing(undefined);
  }, [busy, formDirty, t]);
  const editorDialogRef = useDialogFocus<HTMLDivElement>(editing !== undefined, closeEditor);
  const removeDialogRef = useDialogFocus<HTMLDivElement>(Boolean(removing), () => { if (!busy) setRemoving(null); });
  useEffect(() => {
    if (editing === undefined) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!formDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [closeEditor, editing, formDirty]);
  const toggleRole = (code: string) => setForm(current => ({...current, roles: current.roles.includes(code)
    ? current.roles.filter(role => role !== code) : [...current.roles, code]}));

  const localError = (requestError: any) => {
    const status = requestError.response?.status;
    const code = String(requestError.response?.data?.code || "");
    if (status === 403) return t("administrators.denied");
    if (code === "last_superadmin_required") return t("administrators.lastSuperadmin");
    if (code === "self_deactivation_forbidden" || code === "self_deletion_forbidden") return t("administrators.selfDelete");
    if (code === "administrator_exists") return t("administrators.duplicate");
    if (code === "identity_user_not_found") return t("administrators.identityNotFound");
    return t("administrators.genericError");
  };

  const resolveIdentity = async () => {
    if (editing || (!form.email.trim() && !form.userId.trim())) return;
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return;
    setIdentityLookup("checking");
    try {
      const response = await api.get("/administrators/resolve", {params: {email: form.email.trim(), userId: form.userId.trim() || undefined}});
      const identityUser = response.data?.user;
      if (identityUser?.userId) {
        setForm(current => ({...current, userId: identityUser.userId, email: identityUser.email || current.email, displayName: current.displayName || identityUser.displayName || ""}));
        setIdentityLookup("found");
      } else {
        setIdentityLookup("missing");
      }
    } catch {
      setIdentityLookup("error");
    }
  };

  const rowActions = (user: AdminUser) => {
    const actions: IMenuAction[] = [];
    const divider = () => {
      if (actions.length && actions[actions.length - 1].type !== "divider") actions.push({type: "divider"});
    };
    if (can("admin.users.update")) {
      actions.push({id: "edit", label: t("administrators.edit"), icon: "pencil"});
      divider();
      actions.push({
        id: user.active ? "deactivate" : "activate",
        label: user.active ? t("administrators.deactivateAction") : t("administrators.activateAction"),
        icon: user.active ? "eye-off" : "eye",
        isDisabled: busy || (user.id === currentUser.id && user.active),
      });
    }
    if (can("admin.users.delete")) {
      divider();
      actions.push({
        id: "delete",
        label: t("administrators.delete"),
        icon: "trash",
        isDanger: true,
        isDisabled: busy || user.id === currentUser.id,
      });
    }
    return actions;
  };

  const handleRowAction = async (actionId: string, record: Record<string, any>) => {
    const user = users.find(item => item.id === record.id);
    if (!user) return;
    if (actionId === "edit") { openEdit(user); return; }
    if (actionId === "delete") { setRemoving(user); return; }
    if (actionId !== "activate" && actionId !== "deactivate") return;
    setBusy(true); setPageError(""); setNotice("");
    try {
      await api.put(`/administrators/${user.id}`, {
        displayName: user.displayName,
        roles: user.roles,
        active: actionId === "activate",
      });
      setNotice(t("administrators.updated"));
      await load(pagination.currentPage);
    } catch (requestError: any) { setPageError(localError(requestError)); }
    finally { setBusy(false); }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault(); setFormError(""); setNotice("");
    if (!form.email.trim() || !form.roles.length) { setFormError(t("administrators.required")); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setFormError(t("administrators.invalidEmail")); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/administrators/${editing.id}`, {displayName: form.displayName, roles: form.roles, active: form.active});
        setNotice(t("administrators.updated"));
      } else {
        await api.post("/administrators", {userId: form.userId.trim(), email: form.email.trim(), displayName: form.displayName.trim(), roles: form.roles, sendInvite: form.sendInvite});
        setNotice(t("administrators.created"));
      }
      clearFormDraft(currentDraftKey);
      setEditing(undefined); await load(pagination.currentPage);
    } catch (requestError: any) { setFormError(localError(requestError)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!removing) return;
    setBusy(true); setPageError(""); setNotice("");
    try {
      await api.delete(`/administrators/${removing.id}`);
      const targetPage = users.length === 1 && pagination.currentPage > 1 ? pagination.currentPage - 1 : pagination.currentPage;
      setRemoving(null); setNotice(t("administrators.deleted")); await load(targetPage);
    } catch (requestError: any) { setPageError(localError(requestError)); setRemoving(null); }
    finally { setBusy(false); }
  };

  return <div className="admin-users">
    <div className="section-heading"><div><span className="eyebrow">{t("rbac")}</span><h2>{t("administrators.title")}</h2><p>{t("administrators.subtitle")}</p></div>
      {can("admin.users.create") && <button type="button" className="primary action-primary" title={t("administrators.add")} onClick={openCreate}>＋ {t("administrators.add")}</button>}
    </div>
    <div className="admin-toolbar"><label className="search admin-search">⌕<input value={query} onChange={event => setQuery(event.target.value)} placeholder={t("administrators.search")} aria-label={t("administrators.search")}/></label>
      <button type="button" className="secondary" title={t("administrators.reload")} onClick={() => load(pagination.currentPage)} disabled={loading}>↻ {t("administrators.reload")}</button></div>
    {notice && <div className="notice" role="status">✓ {notice}</div>}
    {pageError && editing === undefined && <div className="alert" role="alert">{pageError}</div>}
    {loading ? <div className="empty compact"><span className="mini-spinner"/><p>{t("administrators.loading")}</p></div> : filtered.length === 0 ?
      <div className="empty compact"><b>{t("administrators.empty")}</b></div> :
      <div className="table-shell administrators-table"><table><thead><tr><th className="action-menu-column">{t("administrators.actions")}</th><th>{t("administrators.name")}</th><th>{t("administrators.userId")}</th><th>{t("administrators.roles")}</th><th>{t("administrators.status")}</th></tr></thead>
        <tbody>{filtered.map(user => <tr key={user.id}><td className="action-menu-cell"><ActionMenu record={user as unknown as Record<string, any>} actions={rowActions(user)} onAction={handleRowAction} triggerLabel={`${t("administrators.actions")}: ${user.email}`}/></td><td><div className="admin-identity"><span className="avatar">{(user.displayName || user.email).slice(0, 2).toUpperCase()}</span><div><b>{user.displayName || user.email}</b><small>{user.email}</small></div>{user.id === currentUser.id && <em>{t("administrators.current")}</em>}</div></td>
          <td>{user.userId ? <code title={user.userId}>{user.userId}</code> : <span className="pending-badge" title={t("administrators.pendingIdentity")}>{t("administrators.pendingIdentity")}</span>}</td><td><div className="role-list">{user.roles.map(role => <span key={role}>{role}</span>)}</div></td>
          <td><span className={user.active ? "state active" : "state inactive"}><i/>{user.active ? t("administrators.active") : t("administrators.inactive")}</span></td></tr>)}</tbody></table></div>}
    {!loading && pagination.count > 0 && <Pagination {...pagination} pageSize={itemsPerPage} disabled={busy} onPageChange={page => load(page)} onPageSizeChange={pageSize => load(1, pageSize)}/>}

    {editing !== undefined && <div className="modal-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) closeEditor(); }}>
      <div ref={editorDialogRef} tabIndex={-1} className="modal" role="dialog" aria-modal="true" aria-labelledby="administrator-form-title">
        <button type="button" className="modal-close" title={t("administrators.close")} aria-label={t("administrators.close")} onClick={closeEditor}>×</button>
        <span className="eyebrow">{t("idExpressAccess")}</span><h2 id="administrator-form-title">{editing ? t("administrators.edit") : t("administrators.add")}</h2><p>{editing ? t("administrators.editHelp") : t("administrators.addHelp")}</p>
        <form onSubmit={submit}><div className="form-grid">
          <label>{t("administrators.email")}<input required type="email" maxLength={320} value={form.email} disabled={Boolean(editing)} onBlur={resolveIdentity} onChange={event => { setIdentityLookup("idle"); setForm({...form, email: event.target.value}); }}/></label>
          <label>{t("administrators.userId")}<input maxLength={64} value={form.userId} disabled={Boolean(editing)} onBlur={resolveIdentity} onChange={event => { setIdentityLookup("idle"); setForm({...form, userId: event.target.value}); }}/></label>
          {!editing && identityLookup !== "idle" && <div className={`identity-lookup identity-lookup-${identityLookup}`} role="status">{t(`administrators.identityLookup.${identityLookup}`)}</div>}
          <label className="full">{t("administrators.name")}<input maxLength={200} value={form.displayName} onChange={event => setForm({...form, displayName: event.target.value})}/></label>
        </div>{!editing && <label className="switch-row invite-row" title={t("administrators.sendInviteHelp")}><span><b>{t("administrators.sendInvite")}</b><small>{t("administrators.sendInviteHelp")}</small></span><input type="checkbox" checked={form.sendInvite} onChange={event => setForm({...form, sendInvite: event.target.checked})}/><i/></label>}<fieldset><legend>{t("administrators.roles")} <small>{form.roles.length} {t("administrators.selected")}</small></legend><p>{t("administrators.roleHelp")}</p><div className="role-options">{roles.map(role => <label key={role.code} className={form.roles.includes(role.code) ? "selected" : ""}><input type="checkbox" checked={form.roles.includes(role.code)} onChange={() => toggleRole(role.code)}/><span><b>{role.name}</b><small>{role.code}{role.description ? ` · ${role.description}` : ""}</small></span><i>✓</i></label>)}</div></fieldset>
          {editing && <label className="switch-row" title={editing.id === currentUser.id ? t("administrators.selfDelete") : t("administrators.activate")}><span><b>{t("administrators.activate")}</b><small>{editing.id === currentUser.id ? t("administrators.selfDelete") : t("administrators.activeHelp")}</small></span><input type="checkbox" disabled={editing.id === currentUser.id} checked={form.active} onChange={event => setForm({...form, active: event.target.checked})}/><i/></label>}
          {formError && <div className="alert form-alert" role="alert">{formError}</div>}
          <div className="modal-actions"><button type="button" className="secondary" title={t("administrators.cancel")} onClick={closeEditor} disabled={busy}>{t("administrators.cancel")}</button><button type="submit" className="primary" title={editing ? t("administrators.save") : t("administrators.create")} disabled={busy}>{busy ? t("administrators.saving") : editing ? t("administrators.save") : t("administrators.create")}</button></div>
        </form>
      </div></div>}

    {removing && <div className="modal-backdrop" role="presentation"><div ref={removeDialogRef} tabIndex={-1} className="modal confirm-modal" role="alertdialog" aria-modal="true" aria-labelledby="delete-administrator-title">
      <div className="danger-symbol">!</div><h2 id="delete-administrator-title">{t("administrators.confirmTitle")}</h2><p>{t("administrators.confirmText")}</p><div className="delete-target"><b>{removing.displayName || removing.email}</b><small>{removing.email}</small></div>
      <div className="modal-actions"><button type="button" className="secondary" title={t("administrators.cancel")} onClick={() => setRemoving(null)} disabled={busy}>{t("administrators.cancel")}</button><button type="button" className="danger-button" title={t("administrators.remove")} onClick={remove} disabled={busy}>{busy ? t("administrators.deleting") : t("administrators.remove")}</button></div>
    </div></div>}
  </div>;
}
